/**
 * Pure helpers over the edit session's `AssignmentMap` (adapter key → party ids).
 *
 * Kept free of React so the rules the ticket cares about — one adapter per party **per feed**,
 * dirty detection across every tab, the full-state PUT body — are unit-testable on their own.
 */
import { AdapterRow, AssignmentMap, AssignmentPayloadEntry, Party } from './types';

export const buildAssignmentMap = (adapters: AdapterRow[]): AssignmentMap =>
  Object.fromEntries(adapters.map((adapter) => [adapter.key, adapter.parties.map((p) => p.party_id)]));

const sameSet = (a: string[] = [], b: string[] = []): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

/** True when any adapter's party set differs — order inside a row does not count as a change. */
export const isAssignmentMapDirty = (original: AssignmentMap, current: AssignmentMap): boolean => {
  const keys = new Set([...Object.keys(original), ...Object.keys(current)]);
  for (const key of keys) {
    if (!sameSet(original[key], current[key])) return true;
  }
  return false;
};

export const addPartyToAdapter = (map: AssignmentMap, adapterKey: string, partyId: string): AssignmentMap => {
  const existing = map[adapterKey] ?? [];
  if (existing.includes(partyId)) return map;
  return { ...map, [adapterKey]: [...existing, partyId] };
};

export const removePartyFromAdapter = (map: AssignmentMap, adapterKey: string, partyId: string): AssignmentMap => {
  const existing = map[adapterKey] ?? [];
  if (!existing.includes(partyId)) return map;
  return { ...map, [adapterKey]: existing.filter((id) => id !== partyId) };
};

/** Party ids assigned to ANY adapter of the given feed type in the working state. */
export const assignedPartyIdsForFeed = (
  map: AssignmentMap,
  adapters: AdapterRow[],
  feedType: AdapterRow['feed_type'],
): Set<string> => {
  const ids = new Set<string>();
  adapters
    .filter((adapter) => adapter.feed_type === feedType)
    .forEach((adapter) => (map[adapter.key] ?? []).forEach((id) => ids.add(id)));
  return ids;
};

/**
 * The "Add party" menu for one adapter row: every active party not already assigned to an
 * adapter **for that row's feed type**. The same party may still be offered on another tab —
 * exclusivity is per feed, not global (ticket § Cross-adapter exclusivity).
 */
export const availablePartiesForAdapter = (
  map: AssignmentMap,
  adapters: AdapterRow[],
  parties: Party[],
  adapterKey: string,
): Party[] => {
  const adapter = adapters.find((row) => row.key === adapterKey);
  if (!adapter) return [];
  const taken = assignedPartyIdsForFeed(map, adapters, adapter.feed_type);
  return parties
    .filter((party) => !taken.has(party.id))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

/**
 * The PUT body: the desired party set for EVERY adapter row the page loaded, including rows the
 * session never touched, so the backend can diff against a complete picture.
 */
export const toAssignmentsPayload = (map: AssignmentMap, adapters: AdapterRow[]): AssignmentPayloadEntry[] =>
  adapters.map((adapter) => ({ adapter_key: adapter.key, party_ids: [...(map[adapter.key] ?? [])] }));

/** Distinct parties with at least one assignment on any feed — the footer's numerator. */
export const countAssignedParties = (map: AssignmentMap): number =>
  new Set(Object.values(map).flat()).size;

/** id → display name, live party list first, stored assignment names as the fallback. */
export const buildPartyNameLookup = (parties: Party[], adapters: AdapterRow[]): Map<string, string> => {
  const lookup = new Map<string, string>();
  adapters.forEach((adapter) =>
    adapter.parties.forEach((p) => {
      if (p.party_name) lookup.set(p.party_id, p.party_name);
    }),
  );
  parties.forEach((party) => lookup.set(party.id, party.name));
  return lookup;
};
